import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const QrCodeAppDownloadForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'QR_Code_App_Download.pdf' : 'QR_Code_App_Download_EN.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  const apps = [
    { name: language === 'marathi' ? 'मेरी पंचायत' : 'Meri Panchayat' },
    { name: language === 'marathi' ? 'पंचायत निर्णय' : 'Panchayat Nirnay' },
    { name: language === 'marathi' ? 'ग्राम संवाद' : 'Gram Samvad' }
  ];

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          .page {
            width: 210mm;
            height: 297mm;
            padding: 20mm;
            margin: 0 auto;
            box-sizing: border-box;
            background-color: white;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
            position: relative;
            page-break-after: always;
          }
          .page:last-child {
            page-break-after: auto;
          }
          
          .dashed-line {
            border-bottom: 1px dashed black;
            display: inline-block;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              margin: 0 !important;
              box-shadow: none !important;
              width: 210mm !important;
              height: 297mm !important;
              border: none !important;
              page-break-after: always !important;
            }
            .page:last-child {
              page-break-after: auto !important;
            }
            .no-print {
              display: none !important;
            }
            .min-h-screen {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>

      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4 no-print">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div ref={componentRef} className="flex flex-col space-y-8 print:space-y-0">
        {apps.map((app, index) => (
          <div key={index} className="page text-black flex flex-col items-center pt-16 px-12 relative">
            
            {/* Header */}
            <h1 className="text-[34px] font-bold text-[#0070c0] mb-8 text-center">
              {language === 'marathi' ? 'मुख्यमंत्री समृद्ध पंचायतराज अभियान' : 'Chief Minister Samruddha Panchayat Raj Abhiyan'}
            </h1>

            {/* Grampanchayat Info */}
            <div className="w-full flex flex-col items-center space-y-4 mb-10 text-[18px] font-bold px-8">
              <div className="flex items-end justify-center w-full">
                <span className="whitespace-nowrap">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                <span className="dashed-line w-[80mm] h-4 mx-2"></span>
                <span className="whitespace-nowrap">{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                <span className="dashed-line flex-grow h-4 ml-2"></span>
              </div>
              <div className="flex items-end w-full pl-[40mm]">
                <span className="whitespace-nowrap">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                <span className="dashed-line w-[60mm] h-4 ml-2"></span>
              </div>
            </div>

            {/* App Download Text */}
            <div className="text-[22px] font-bold mb-6 text-center">
              <span className="text-[#c00000]">{app.name}</span>
              <span className="text-black"> {language === 'marathi' ? 'ॲप डाऊनलोड करण्यासाठी खालील' : 'To download the app, below'}</span>
            </div>

            {/* QR Code Instruction */}
            <div className="text-[22px] font-bold mb-8 text-center">
              {language === 'marathi' ? 'QR CODE SCAN करा' : 'SCAN QR CODE'}
            </div>

            {/* QR Code Box */}
            <div className="border border-black p-6 flex items-center justify-center w-[140mm] h-[140mm] mb-12">
              {/* Using a highly detailed generic QR code SVG path */}
              <svg width="100%" height="100%" viewBox="0 0 33 33" shapeRendering="crispEdges">
                 <path fill="#000000" d="M3,3h7v7H3V3z M4,4v5h5V4H4z M6,6h1v1H6V6z M23,3h7v7h-7V3z M24,4v5h5V4H24z M26,6h1v1h-1V6z M3,23h7v7H3V23z M4,24v5h5v-5H4z M6,26h1v1H6V26z M12,3h1v1h-1V3z M14,3h2v1h-2V3z M17,3h1v1h-1V3z M19,3h3v1h-3V3z M12,4h2v1h-2V4z M15,4h2v1h-2V4z M19,4h1v1h-1V4z M21,4h1v1h-1V4z M13,5h1v1h-1V5z M16,5h3v1h-3V5z M20,5h1v1h-1V5z M12,6h1v1h-1V6z M15,6h1v1h-1V6z M17,6h1v1h-1V6z M19,6h1v1h-1V6z M21,6h1v1h-1V6z M12,7h2v1h-2V7z M15,7h2v1h-2V7z M18,7h1v1h-1V7z M20,7h2v1h-2V7z M12,8h1v1h-1V8z M14,8h1v1h-1V8z M16,8h3v1h-3V8z M21,8h1v1h-1V8z M3,11h1v1H3V11z M5,11h3v1H5V11z M9,11h2v1H9V11z M12,11h1v1h-1V11z M14,11h1v1h-1V11z M16,11h2v1h-2V11z M19,11h1v1h-1V11z M21,11h3v1h-3V11z M25,11h2v1h-2V11z M28,11h2v1h-2V11z M3,12h2v1H3V12z M6,12h1v1H6V12z M8,12h1v1H8V12z M10,12h2v1h-2V12z M13,12h1v1h-1V12z M15,12h2v1h-2V12z M18,12h1v1h-1V12z M22,12h1v1h-1V12z M24,12h2v1h-2V12z M27,12h1v1h-1V12z M29,12h1v1h-1V12z M4,13h1v1H4V13z M7,13h2v1H7V13z M11,13h2v1h-2V13z M14,13h1v1h-1V13z M16,13h1v1h-1V13z M18,13h3v1h-3V13z M22,13h2v1h-2V13z M25,13h1v1h-1V13z M28,13h2v1h-2V13z M3,14h3v1H3V14z M8,14h1v1H8V14z M10,14h1v1H10V14z M12,14h2v1h-2V14z M16,14h1v1h-1V14z M18,14h2v1h-2V14z M21,14h1v1h-1V14z M23,14h1v1h-1V14z M25,14h2v1h-2V14z M28,14h2v1h-2V14z M5,15h1v1H5V15z M7,15h1v1H7V15z M9,15h3v1H9V15z M14,15h1v1h-1V15z M16,15h1v1h-1V15z M18,15h2v1h-2V15z M22,15h3v1h-3V15z M26,15h1v1h-1V15z M28,15h1v1h-1V15z M3,16h2v1H3V16z M6,16h2v1H6V16z M10,16h1v1h-1V16z M12,16h1v1h-1V16z M14,16h2v1h-2V16z M17,16h1v1h-1V16z M20,16h1v1h-1V16z M23,16h1v1h-1V16z M25,16h1v1h-1V16z M27,16h3v1h-3V16z M3,17h1v1H3V17z M5,17h1v1H5V17z M7,17h1v1H7V17z M9,17h1v1H9V17z M11,17h2v1h-2V17z M14,17h1v1h-1V17z M17,17h2v1h-2V17z M20,17h2v1h-2V17z M23,17h2v1h-2V17z M26,17h2v1h-2V17z M4,18h1v1H4V18z M6,18h2v1H6V18z M9,18h1v1H9V18z M11,18h1v1h-1V18z M13,18h2v1h-2V18z M16,18h2v1h-2V18z M19,18h2v1h-2V18z M22,18h1v1h-1V18z M24,18h1v1h-1V18z M26,18h1v1h-1V18z M28,18h2v1h-2V18z M3,19h3v1H3V19z M7,19h2v1H7V19z M10,19h1v1H10V19z M13,19h1v1h-1V19z M15,19h1v1h-1V19z M17,19h2v1h-2V19z M20,19h1v1h-1V19z M22,19h2v1h-2V19z M25,19h2v1h-2V19z M28,19h1v1h-1V19z M5,20h1v1H5V20z M7,20h1v1H7V20z M11,20h1v1h-1V20z M13,20h2v1h-2V20z M16,20h1v1h-1V20z M18,20h3v1h-3V20z M22,20h1v1h-1V20z M24,20h1v1h-1V20z M26,20h4v1h-4V20z M3,21h1v1H3V21z M5,21h2v1H5V21z M9,21h3v1H9V21z M14,21h2v1h-2V21z M17,21h2v1h-2V21z M21,21h2v1h-2V21z M24,21h2v1h-2V21z M27,21h1v1h-1V21z M29,21h1v1h-1V21z M11,23h2v1h-2V23z M14,23h1v1h-1V23z M16,23h2v1h-2V23z M19,23h1v1h-1V23z M21,23h2v1h-2V23z M24,23h2v1h-2V23z M27,23h1v1h-1V23z M29,23h1v1h-1V23z M11,24h1v1h-1V24z M13,24h1v1h-1V24z M16,24h1v1h-1V24z M18,24h1v1h-1V24z M20,24h2v1h-2V24z M23,24h1v1h-1V24z M25,24h1v1h-1V24z M27,24h2v1h-2V24z M11,25h1v1h-1V25z M13,25h2v1h-2V25z M16,25h2v1h-2V25z M19,25h1v1h-1V25z M22,25h2v1h-2V25z M26,25h1v1h-1V25z M28,25h2v1h-2V25z M12,26h1v1h-1V26z M14,26h1v1h-1V26z M16,26h1v1h-1V26z M18,26h2v1h-2V26z M21,26h1v1h-1V26z M23,26h2v1h-2V26z M26,26h1v1h-1V26z M28,26h1v1h-1V26z M11,27h2v1h-2V27z M14,27h2v1h-2V27z M17,27h1v1h-1V27z M19,27h3v1h-3V27z M23,27h1v1h-1V27z M25,27h2v1h-2V27z M28,27h2v1h-2V27z M11,28h1v1h-1V28z M13,28h2v1h-2V28z M16,28h1v1h-1V28z M18,28h1v1h-1V28z M21,28h2v1h-2V28z M24,28h1v1h-1V28z M26,28h1v1h-1V28z M28,28h1v1h-1V28z M11,29h2v1h-2V29z M14,29h1v1h-1V29z M16,29h3v1h-3V29z M20,29h1v1h-1V29z M23,29h1v1h-1V29z M25,29h2v1h-2V29z M28,29h2v1h-2V29z" />
              </svg>
            </div>

            {/* Footer Link */}
            <div className="absolute bottom-10 left-0 right-0 text-center text-[#0000ee] text-sm underline print:hidden">
              www.majhigrampanchayat.com
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QrCodeAppDownloadForm;
